import axios from 'axios';
import { FASTAPI_APP_BASE_URL } from './config';
import { getStoredToken, getStoredUser } from '../stores/authStore';

// Types
export interface UserSkillResponse {
  skill_id: string;
  skill_code: string;
  skill_name: string;
  proficiency: number;
  confidence: number;
  last_practiced?: string;
  total_attempts?: number;
  successful_attempts?: number;
  first_seen?: string;
  last_assessed?: string;
}

export interface SkillStatisticsResponse {
  total_skills: number;
  mastered: number;
  learning: number;
  struggling: number;
  skills_by_subject?: Record<string, {
    total: number;
    average_mastery: number;
  }>;
  recent_progress?: Array<{
    skill_name: string;
    mastery: number;
    last_practiced: string;
  }>;
  review_needed?: Array<{
    skill_name: string;
    mastery: number;
    days_overdue: number;
  }>;
}

export interface LearningJourneyResponse {
  user_id: number;
  observations?: Array<{
    id: number;
    observation_type: string;
    content: any;
    confidence: number;
    created_at: string;
  }>;
  teaching_log?: Array<{
    id: number;
    strategy_used: string;
    effectiveness: number;
    notes?: string;
    created_at: string;
  }>;
  interests?: Array<{
    topic: string;
    intensity: number;
    first_mentioned: string;
    last_mentioned: string;
  }>;
  misconceptions?: Array<{
    concept: string;
    description: string;
    corrected: boolean;
    created_at: string;
  }>;
}

export interface PersonalizationSummaryResponse {
  has_data: boolean;
  interests?: Array<{
    topic: string;
    intensity: number;
  }>;
  learning_patterns?: Array<{
    pattern_type: string;
    description: string;
    confidence: number;
  }>;
  effective_strategies?: string[];
  misconceptions?: Array<{
    concept: string;
    description: string;
    corrected: boolean;
  }>;
  recommendations?: string[];
}

export interface ProfileAnalysisResponse {
  status: string;
  summary?: string;
  learning_style?: string;
  knowledge_domains?: Record<string, number>;
  confidence?: number;
  recommendations?: string[];
  error?: string;
}

export interface LearningPathResponse {
  target_skill: {
    skill_id: string;
    skill_code: string;
    skill_name: string;
  };
  path_length: number;
  estimated_time?: string;
  skills: Array<{
    skill_id: string;
    skill_code: string;
    skill_name: string;
    difficulty?: number;
    order: number;
    prerequisites_met: boolean;
  }>;
}

export interface PrerequisiteCheckResponse {
  skill_id: string;
  prerequisites_met: boolean;
  missing_skills?: Array<{
    skill_id: string;
    skill_code: string;
    skill_name: string;
    current_proficiency: number;
    required_proficiency: number;
  }>;
}

class PersonalizationService {
  private static instance: PersonalizationService;
  private baseURL: string;

  private constructor() {
    // Personalization + skill-graph endpoints live on FastAPI, not the worker proxy
    this.baseURL = `${FASTAPI_APP_BASE_URL}/api/v1`;
  }

  public static getInstance(): PersonalizationService {
    if (!PersonalizationService.instance) {
      PersonalizationService.instance = new PersonalizationService();
    }
    return PersonalizationService.instance;
  }

  private getAuthHeaders() {
    // Prefer Zustand auth store token; fallback to localStorage for legacy paths
    let token: string | null = null;
    try { token = getStoredToken(); } catch {}
    if (!token) {
      try { token = localStorage.getItem('token'); } catch {}
    }
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  // Skill Graph endpoints
  async getUserSkills(): Promise<UserSkillResponse[]> {
    try {
      const response = await axios.get(
        `${this.baseURL}/skills/graph/users/me/skills`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching user skills:', error);
      throw error;
    }
  }

  async getSkillStatistics(): Promise<SkillStatisticsResponse> {
    try {
      const response = await axios.get(
        `${this.baseURL}/skills/graph/users/me/skills/statistics`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching skill statistics:', error);
      throw error;
    }
  }

  async checkPrerequisites(skillId: string): Promise<PrerequisiteCheckResponse> {
    try {
      const response = await axios.get(
        `${this.baseURL}/skills/graph/users/me/skills/${skillId}/prerequisites-check`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking prerequisites:', error);
      throw error;
    }
  }

  async getLearningPath(targetSkillId: string): Promise<LearningPathResponse> {
    try {
      const response = await axios.get(
        `${this.baseURL}/skills/graph/users/me/learning-path/${targetSkillId}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching learning path:', error);
      throw error;
    }
  }

  // Learning Journey endpoints
  async getLearningJourney(userId?: number): Promise<LearningJourneyResponse> {
    try {
      // If no userId provided, try to get from auth store or localStorage
      if (!userId) {
        try {
          const user = getStoredUser();
          if (user) {
            userId = (user as any).user_id || (user as any).id;
          }
        } catch {}
        if (!userId) {
          try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
              const user = JSON.parse(userStr);
              userId = user.user_id || user.id;
            }
          } catch {}
        }
      }
      
      if (!userId) {
        throw new Error('User ID not found');
      }

      const response = await axios.get(
        `${this.baseURL}/learning/learning-journey/${userId}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching learning journey:', error);
      throw error;
    }
  }

  async getPersonalizationSummary(userId?: number): Promise<PersonalizationSummaryResponse> {
    try {
      // If no userId provided, try to get from auth store or localStorage
      if (!userId) {
        try {
          const user = getStoredUser();
          if (user) {
            userId = (user as any).user_id || (user as any).id;
          }
        } catch {}
        if (!userId) {
          try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
              const user = JSON.parse(userStr);
              userId = user.user_id || user.id;
            }
          } catch {}
        }
      }
      
      if (!userId) {
        throw new Error('User ID not found');
      }

      const response = await axios.get(
        `${this.baseURL}/learning/learning-journey/${userId}/personalization-summary`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching personalization summary:', error);
      throw error;
    }
  }

  // Profile Analysis endpoints
  // analyzeUserProfile deprecated and removed server-side

  async getUserProfile(userId?: number): Promise<any> {
    try {
      // If no userId provided, try to get from auth store or localStorage
      if (!userId) {
        try {
          const user = getStoredUser();
          if (user) {
            userId = (user as any).user_id || (user as any).id;
          }
        } catch {}
        if (!userId) {
          try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
              const user = JSON.parse(userStr);
              userId = user.user_id || user.id;
            }
          } catch {}
        }
      }
      
      if (!userId) {
        throw new Error('User ID not found');
      }

      const response = await axios.get(
        `${this.baseURL}/users/${userId}/profile`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  // Batch update skills (for testing)
  async batchUpdateSkills(updates: Array<{
    skill_id: string;
    proficiency?: number;
    confidence?: number;
  }>): Promise<UserSkillResponse[]> {
    try {
      const response = await axios.post(
        `${this.baseURL}/skill-graph/users/me/skills/batch-update`,
        { updates },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error batch updating skills:', error);
      throw error;
    }
  }

  // Search skills
  async searchSkills(query: string, limit: number = 10): Promise<any[]> {
    try {
      const response = await axios.get(
        `${this.baseURL}/skill-graph/skills/search`,
        {
          params: { q: query, limit },
          headers: this.getAuthHeaders()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error searching skills:', error);
      throw error;
    }
  }
}

export default PersonalizationService.getInstance();
